/**
 * Shop API 함수들
 */

import { AuthContextType } from "../auth/AuthContext";
import { apiWithTimeout } from "../../shared/api/timeout";
import { apiHost, isPayloadError, getErrorMessage } from "../../shared/api/helpers";

const isProd = import.meta.env.VITE_APP_ENV === "PROD";

export interface FetchShopListOpts {
    auth: AuthContextType,
    tested?: boolean,
    setErrorMessage?: (arg: any) => void;
    setResult?: (arg: any) => void;
}

export interface FetchShopOpts {
    auth: AuthContextType,
    id: string,
    tested?: boolean,
    setErrorMessage?: (arg: any) => void;
    setResult?: (arg: any) => void;
}

export interface CreateShopOpts {
    auth: AuthContextType,
    payload: {
        market_site: string,
        name: string,
        api_key: string,
        api_secret: string,
        seller_center_id?: string,
        seller_center_pw?: string,
        url?: string,
    },
    setProcessMessage?: (arg?: any) => void;
    setErrorMessage?: (arg?: any) => void;
    setResult?: (arg?: any) => void;
    setError?: (...arg: any) => void;
}

export interface UpdateShopOpts {
    id: string,
    payload: object,
    auth: AuthContextType,
    setErrorMessage?: (arg: any) => void;
    setProcessMessage?: (arg: any) => void;
    setResult?: (arg?: any) => void;
    setError?: (...arg: any) => void;
}

export interface deleteShopOpts {
    id: string,
    auth: AuthContextType,
    setErrorMessage?: (arg: any) => void;
    setProcessMessage?: (arg: any) => void;
    setResult?: () => void;
}


export interface SearchUserShopItemOpts {
    auth: AuthContextType,
    shop_id: string,
    search_keyword: string,
    page?: number,
}

export interface FetchUserShopItemDetailOpts {
    auth: AuthContextType,
    shop_id: string,
    shop_item_gid: string,
}

export interface FetchUserShopItemImageUrlOpts {
    auth: AuthContextType,
    shop_id: string,
    shop_item_id: string,
}

export interface FetchUserShopOriginProductIdOpts {
    auth: AuthContextType,
    shop_id: string,
    channel_product_id: string,
}


export async function fetchShopList(opts: FetchShopListOpts) {
    const { auth, tested, setErrorMessage, setResult } = opts;
    try {
        let url = `${apiHost}/api/shops/`
        if (tested) {
            url += "?tested=true"
        }
        const response = await apiWithTimeout.quick.get(url);
        if (response.data && setResult) {
            setResult(response.data);
        }
    } catch (error: any) {
        if (error.response?.status === 401) {
            await auth?.logout();
        }
        const message = getErrorMessage(error);
        if (setErrorMessage) {
            setErrorMessage(message);
        } else {
            console.error(message);
        }
    }
}


export async function fetchShop(opts: FetchShopOpts) {
    const { auth, id, tested, setErrorMessage, setResult } = opts;
    try {
        let url = `${apiHost}/api/shops/${id}/`
        if (tested) {
            url += "?tested=true"
        }
        const response = await apiWithTimeout.quick.get(url);
        if (response.data && setResult) {
            setResult(response.data);
        } else {
            return response.data;
        }
    } catch (error: any) {
        if (error.response?.status === 401) {
            await auth?.logout();
        }
        const message = getErrorMessage(error);
        if (setErrorMessage) {
            setErrorMessage(message);
        } else {
            console.error(message);
        }
    }
}

export async function fetchUserShop(usershop_id: string, auth: AuthContextType) {
    try {
        const response = await apiWithTimeout.quick.get(`${apiHost}/api/shops/${usershop_id}/`);
        return response.data;
    } catch (error: any) {
        if (error.response?.status === 401) {
            await auth?.logout();
        }
        throw new Error(error.response?.statusText || 'Network error');
    }
}

export async function createShop(opts: CreateShopOpts) {
    const { auth, payload, setErrorMessage, setProcessMessage, setResult, setError } = opts;

    try {
        await apiWithTimeout.normal.post(`${apiHost}/api/shops/`, payload);
        if (setResult) {
            setResult();
        }
    } catch (error: any) {
        if (error.response?.status === 401) {
            auth?.logout();
            return;
        }

        let errorMessage = "시스템 오류입니다. 잠시 후 다시 시도하세요";
        const errorData = error.response?.data;

        // API 접속 테스트 실패 시 명확한 메시지 제공
        if (errorData && (errorData.api_key || errorData.api_secret)) {
            errorMessage = "API 접속 테스트에 실패했습니다. 접속 정보를 다시 확인해주세요.";
        }

        if (isPayloadError(errorData, payload) && setError) {
            for (const [key, value] of Object.entries(errorData)) {
                setError(key, { type: "custom", message: value });
            }
        } else if (setErrorMessage) {
            setErrorMessage(errorMessage);
        }
    } finally {
        if (setProcessMessage) {
            setProcessMessage(null);
        }
    }
}

export async function updateShop(opts: UpdateShopOpts) {
    const { id, payload, auth, setErrorMessage, setProcessMessage, setResult, setError } = opts;

    try {
        await apiWithTimeout.normal.patch(`${apiHost}/api/shops/${id}/`, { ...payload, id });
        await updateShopTestedAt(auth, id, true);
        setResult && setResult(); // Redirect to mypage
    } catch (error: any) {
        if (error.response?.status === 401) {
            auth?.logout();
            return;
        }

        let errorMessage = "시스템 오류입니다. 잠시 후 다시 시도하세요";
        const errorData = error.response?.data;

        // API 접속 테스트 실패 시 명확한 메시지 제공
        if (errorData && (errorData.api_key || errorData.api_secret)) {
            errorMessage = "API 접속 테스트에 실패했습니다. 접속 정보를 다시 확인해주세요.";
        }

        if (isPayloadError(errorData, payload) && setError) {
            for (const [key, value] of Object.entries(errorData)) {
                setError(key, { type: "custom", message: value });
            }
        } else if (setErrorMessage) {
            setErrorMessage(errorMessage);
        }
    } finally {
        if (setProcessMessage) {
            setProcessMessage(null);
        }
    }
}

export async function updateShopTestedAt(
    auth: AuthContextType,
    uid: string, // shop_id
    tested: boolean
) {
    try {
        await apiWithTimeout.normal.patch(`${apiHost}/api/shops/${uid}/tested`, { tested });
    } catch (error: any) {
        if (error.response?.status === 401) {
            auth?.logout();
            return;
        }
    }
}

export async function deleteShop(opts: deleteShopOpts) {
    const { id, auth, setErrorMessage, setProcessMessage, setResult } = opts;

    try {
        await apiWithTimeout.normal.delete(`${apiHost}/api/shops/${id}/`);
        if (setResult) {
            setResult();
        }
    } catch (error: any) {
        if (error.response?.status === 401) {
            auth?.logout();
            return;
        }

        let errorMessage = "시스템 오류입니다. 잠시 후 다시 시도하세요";
        // detect specific error
        if (error.response?.status === 400) {
            console.log(error.response);
            //TODO: consider keep consist error data: `errror` vs `errors`
            if (error.response.data.error) {
                errorMessage = error.response.data.error
            }
        }


        if (setErrorMessage) {
            setErrorMessage(errorMessage);
        }
        if (setProcessMessage) {
            setProcessMessage(null);
        }
    }
}



// TanStack Query용 함수
export async function searchUserShopItemFn(opts: { shop_id: string; search_keyword: string; page: number }) {
    const { shop_id, search_keyword, page } = opts;
    const url = `${apiHost}/api/shops/${shop_id}/search`;
    const response = await apiWithTimeout.normal.post(url, { search_keyword, page });
    return response.data;
}

// 레거시 함수
export async function searchUserShopItem(opts: SearchUserShopItemOpts) {
    const { auth, shop_id, search_keyword, page } = opts;
    try {
        const url = `${apiHost}/api/shops/${shop_id}/search`;
        const response = await apiWithTimeout.normal.post(url, { search_keyword, page: page ? page : 1 });
        console.log("searchUserShopItem response:", response.data);
        return response.data;
    } catch (error: any) {
        if (error.response?.status === 401) {
            auth?.logout();
        }
        throw new Error(error.response?.statusText || 'Network error');
    }
}

export async function fetchUserShopItemDetail(opts: FetchUserShopItemDetailOpts) {
    const { auth, shop_id, shop_item_gid } = opts;
    try {
        const url = `${apiHost}/api/shops/${shop_id}/items/${shop_item_gid}`;
        const response = await apiWithTimeout.normal.post(url);
        return response.data;
    } catch (error: any) {
        if (error.response?.status === 401) {
            auth?.logout();
        }
        throw new Error(error.response?.statusText || "Network error");
    }
}

export async function fetchUserShopItemImageUrl(opts: FetchUserShopItemImageUrlOpts) {
    const { shop_id, shop_item_id } = opts;
    try {
        const url = `${apiHost}/api/shops/${shop_id}/items/${shop_item_id}/imageurl`;
        const response = await apiWithTimeout.long.post(url);
        return response.data;
    } catch (error: any) {
        console.error("Failed to fetch shop item image:", error);
        throw new Error(error.response?.statusText || "Network error");
    }
}

export async function fetchUserShopOriginProductId(opts: FetchUserShopOriginProductIdOpts) {
    const { auth, shop_id, channel_product_id } = opts;
    try {
        const url = `${apiHost}/api/shops/${shop_id}/origin-product-id`;
        const response = await apiWithTimeout.normal.post(url, { channel_product_id });
        if (!isProd) {
            console.log("fetchUserShopOriginProductId response:", response.data);
        }
        return response.data;
    } catch (error: any) {
        if (error.response?.status === 401) {
            auth?.logout();
        }
        if (!isProd) {
            console.error("Failed to fetch origin product ID:", error.response?.data || error);
        }
        throw new Error(error.response?.data?.error || error.response?.statusText || "Network error");
    }
}
